import React, { useState } from 'react';
import useVideoQuality from '../hooks/useVideoQuality';

const VideoQualitySettings = () => {
  const {
    participantCount,
    networkQuality,
    contentType,
    autoQualityEnabled,
    recommendedQuality,
    setAutoQualityEnabled,
    switchVideoQuality,
    optimizeForContentType,
    isHighQualityRecommended,
    isLowQualityRecommended,
  } = useVideoQuality();

  const [showSettings, setShowSettings] = useState(false);
  const [audioQuality, setAudioQuality] = useState('medium');

  const qualityOptions = [
    { value: 'low', label: 'Low (360p)', description: 'Best for poor connections' },
    { value: 'medium', label: 'Medium (480p)', description: 'Balanced quality' },
    { value: 'high', label: 'High (720p)', description: 'Best quality' }
  ];

  const contentTypeOptions = [
    { value: 'motion', label: 'Motion', description: 'Webcam, videos' },
    { value: 'text', label: 'Text/Presentation', description: 'Screen sharing with text' },
    { value: 'detail', label: 'Detail', description: 'Art, detailed graphics' }
  ];

  const getNetworkIcon = () => {
    switch (networkQuality) {
      case 'high': return '📶';
      case 'medium': return '📶';
      case 'low': return '📱';
      default: return '❓';
    }
  };

  const getQualityColor = (quality) => {
    switch (quality) {
      case 'high': return 'text-green-600';
      case 'medium': return 'text-yellow-600';
      case 'low': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <div className="relative">
      {/* Quality Indicator Button */}
      <button
        onClick={() => setShowSettings(!showSettings)}
        className="flex items-center space-x-2 px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
        title="Video Quality Settings"
      >
        <span className="text-sm">🎥</span>
        <span className={`text-xs font-medium ${getQualityColor(recommendedQuality)}`}>
          {recommendedQuality?.toUpperCase() || 'AUTO'}
        </span>
        <span className="text-xs">{getNetworkIcon()}</span>
      </button>

      {/* Settings Panel */}
      {showSettings && (
        <div className="absolute bottom-full mb-2 right-0 w-80 bg-white rounded-lg shadow-lg border border-gray-200 p-4 z-50">
          <div className="space-y-4">
            {/* Status Information */}
            <div className="border-b border-gray-200 pb-3">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Video Quality Status</h3>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-gray-600">Participants:</span>
                  <span className="font-medium ml-1">{participantCount}</span>
                </div>
                <div>
                  <span className="text-gray-600">Network:</span>
                  <span className={`font-medium ml-1 ${getQualityColor(networkQuality)}`}>
                    {networkQuality}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Recommended:</span>
                  <span className={`font-medium ml-1 ${getQualityColor(recommendedQuality)}`}>
                    {recommendedQuality}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Content:</span>
                  <span className="font-medium ml-1">{contentType}</span>
                </div>
              </div>
            </div>

            {/* Auto Quality Toggle */}
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-900">Auto Quality</label>
              <button
                onClick={() => setAutoQualityEnabled(!autoQualityEnabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  autoQualityEnabled ? 'bg-blue-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    autoQualityEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Manual Quality Selection */}
            {!autoQualityEnabled && (
              <div>
                <label className="text-sm font-medium text-gray-900 mb-2 block">
                  Manual Quality
                </label>
                <div className="space-y-2">
                  {qualityOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        switchVideoQuality(option.value)
                        setAudioQuality(option.value);
                      } }
                      className={`w-full text-left p-2 rounded-md border transition-colors ${
                      audioQuality === option.value
                        ? 'bg-blue-50 border-blue-300 text-blue-900'
                        : 'hover:bg-gray-100 border-gray-200'
                    }`}
                    >
                      <div className="font-medium text-sm">{option.label}</div>
                      <div className="text-xs text-gray-600">{option.description}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Content Type Selection */}
            <div>
              <label className="text-sm font-medium text-gray-900 mb-2 block">
                Content Optimization
              </label>
              <div className="space-y-2">
                {contentTypeOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => optimizeForContentType(option.value)}
                    className={`w-full text-left p-2 rounded-md border transition-colors ${
                      contentType === option.value
                        ? 'bg-blue-50 border-blue-300 text-blue-900'
                        : 'hover:bg-gray-100 border-gray-200'
                    }`}
                  >
                    <div className="font-medium text-sm">{option.label}</div>
                    <div className="text-xs text-gray-600">{option.description}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Performance Hints */}
            <div className="border-t border-gray-200 pt-3">
              <div className="text-xs text-gray-600">
                {isLowQualityRecommended && (
                  <div className="flex items-center space-x-2 text-orange-600">
                    <span>⚠️</span>
                    <span>Low quality recommended for current conditions</span>
                  </div>
                )}
                {isHighQualityRecommended && (
                  <div className="flex items-center space-x-2 text-green-600">
                    <span>✅</span>
                    <span>High quality supported</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoQualitySettings;